import type { PrefetchedQueryState } from '@change-corgi/core/react/async';
import { prefetchQuery, usePrefetchableQuery } from '@change-corgi/core/react/async';
import type { UtilityContext } from '@change-corgi/core/react/utilityContext';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import type { PrefetchUserContext } from 'src/shared/prefetch';
import { createPrefetchState } from 'src/shared/prefetch';
import type { Session } from 'src/shared/session';

import { getInitialPrefetchUserDataQueryId, usePrefetchUserDataQueryId } from 'src/app/shared/hooks/query';
import { useSessionAsync } from 'src/app/shared/hooks/session';
import { isLoaded } from 'src/app/shared/utils/async';

import { getUserInfo } from '../petitionInfo/api/userInfo';
import type { UserInfo, UserInfoState } from '../types';

export type SignerState = PrefetchedQueryState<{ info: UserInfo }>;

export function useUserInfo(slug: string, prefetchedUserInfo?: UserInfo): UserInfoState {
	const utilityContext = useUtilityContext();
	const sessionState = useSessionAsync();
	const session = isLoaded(sessionState) ? sessionState.value : undefined;
	const queryId = `${slug}:${usePrefetchUserDataQueryId()}`;

	return usePrefetchableQuery(
		async () => ({ info: await getUserInfo(session, slug, utilityContext) }),
		[session, slug, utilityContext],
		{
			id: queryId,
			prefetchedState:
				prefetchedUserInfo === undefined
					? undefined
					: createPrefetchState(queryId, 'loaded', { info: prefetchedUserInfo }),
			errorHandler: (e: Error) => ({ cause: e }),
		},
	);
}

export const prefetchUserInfo = async (
	session: Session | undefined,
	slug: string,
	utilityContext: UtilityContext,
): Promise<UserInfo | undefined> => {
	const state = await prefetchQuery(async () => ({ info: await getUserInfo(session, slug, utilityContext) }), {
		id: `${slug}:${getInitialPrefetchUserDataQueryId()}`,
		errorHandler: (e: Error) => ({ cause: e }),
	});
	return isLoaded(state) ? state.info : undefined;
};

export const prefetchUserDataUserInfo = async (context: PrefetchUserContext): Promise<UserInfo> => {
	const {
		params: { slug },
		utilityContext,
		session,
	} = context;

	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	const userInfo = await prefetchUserInfo(session, slug!, utilityContext);
	return userInfo || { canEdit: false, ownPetition: false, signed: false };
};
